import { ReportOverviewScreen } from "@/components/screens/report/overview/OverviewScreen"
import { useCreateCheckoutSession } from "@/queries/useCheckoutSession"
import { useNavigate, useParams } from "@/router"
import { MileageFormData } from "@/state/draft/mileage"
import { PeriodFormData } from "@/state/draft/period"
import { vehicleDraftAtom, VehicleModel } from "@/state/draft/vehicle"
import { sum } from "@/utils/array"
import { getMileageRate } from "@/utils/mileageRate"
import { useAtom, useSetAtom } from "jotai"
import { RATES } from "@/constants/rates"
import { useLoadedReportContext } from "./_context/ReportContext"
import { defaultReportForm, reportFormDraftAtom } from "@/state/report"
import { useUpdateVehicle } from "@/queries/useReport"
import { useAnalytics } from "@/hooks/use-analytics"
import { useEffect } from "react"
import { calcBusinessRate } from "./pdf/_pdfPrepare/generatePDFReport"
import { setCurrentReportId } from "@/state/auth"
import { PlainDate, PlainDateTime } from "@/utils/date"
import { calculateReportFee, monthsBetween } from "@/utils/report"
import { tripsAtom } from "@/state/trips"

export default function ReportPage() {
	const analytics = useAnalytics("FullReport")
	const report = useLoadedReportContext()
	const setReportFormDraft = useSetAtom(reportFormDraftAtom)
	const [trips, setTrips] = useAtom(tripsAtom)

	// const [reportForm, setReportForm] = useAtom(reportFormDraftAtom)
	// const [trips, setTrips] = useAtom(tripsAtom)
	const navigate = useNavigate()

	useEffect(() => {
		analytics.sendEvent(
			report.report.paid ? "FullReport__View" : "ReportPreview__View"
		)
	}, [])

	const { mutateAsync: updateVehicle, isPending: isVehicleUpdating } =
		useUpdateVehicle()
	const setVehicle = useSetAtom(vehicleDraftAtom)

	const { id: reportId } = useParams("/report/:id")
	const { mutateAsync: createCheckoutSession, isPending: isProcessingPayment } =
		useCreateCheckoutSession()

	// TODO: render skeleton
	// if (reportForm.downloading || report.trips.length === 0) return <div></div>

	const range = report.form.period.range.map((x) => PlainDate.from(x))

	const reportDateRange = PlainDate.formatDateRange(range[0], range[1])
	const monthCount = monthsBetween(range[0], range[1])

	const productName = `Mileage Report for ${monthCount} months`

	const handlePayment = () => {
		analytics.sendEvent("GoToCheckout")
		createCheckoutSession({
			months: monthCount,
			reportId,
			productName,
		}).then((response) => {
			window.location.replace(response.payment_url)
		})
	}
	const handleEditNonBayer = () => {
		setTrips([])
		navigate("/report/create/period")
	}
	const handleNewReport = () => {
		setCurrentReportId("")
		setTrips([])
		setReportFormDraft(defaultReportForm)
		navigate("/report/create/period")
	}
	const blockedByPaywall = !report.report.paid

	const handleEdit = () => {
		analytics.sendEvent("Click_EditReport")
		if (blockedByPaywall) {
			handleEditNonBayer()
		} else {
			navigate("/report/:id/edit/period", {
				params: {
					id: reportId,
				},
			})
		}
	}

	const handleExport = () => {
		analytics.sendEvent("Click_ExportPDF")
		navigate("/report/:id/pdf", {
			params: {
				id: reportId,
			},
		})
	}

	const handleUnlock = () => {
		analytics.sendEvent("Click_UnlockFull")
	}

	const mileage = report.form.mileage
	return (
		<ReportOverviewScreen
			onPaywallOpen={() => {
				analytics.sendEvent("Paywall_View")
			}}
			onAddVehicleClick={() => {
				analytics.sendEvent("Click_AddCar")
			}}
			onUnlock={handleUnlock}
			blockedByPaywall={blockedByPaywall}
			vehicle={report.form.vehicle}
			onVehicleChange={(vehicle) => {
				setVehicle(vehicle)
				updateVehicle()
			}}
			isVehicleUpdating={isVehicleUpdating}
			vehicleSummary={{
				total: sum(
					trips,
					(x) => x.mileage * getMileageRate(PlainDate.from(x.date), RATES)
				),
				vehicles: [
					{
						businessMiles: sum(trips, (x) => x.mileage),
						businessValue: sum(
							trips,
							(x) =>
								x.mileage * getMileageRate(PlainDate.from(x.date), RATES)
						),
						odometer:
							mileage.inputMethod === "odometer"
								? +mileage.startOdometer
								: 0,
						name: report.form.vehicle
							? vehicleModelToString(report.form.vehicle)
							: "Default",
						rate: calcBusinessRate(trips),
					},
				],
			}}
			reportFee={calculateReportFee(monthCount)}
			onEditReport={handleEdit}
			onExportReport={handleExport}
			onNewReport={handleNewReport}
			reportDateRangeText={reportDateRange}
			trips={trips.map((x) => ({
				startAddress: x.startAddress,
				finishAddress: x.endAddress,
				date: `${PlainDate.from(
					x.date
				).formatWeekdayDate()}\n${PlainDateTime.fromMinutes(
					x.date,
					x.timeStart
				).formatTime()}`,
				distance: x.mileage,
				amount: x.mileage * getMileageRate(PlainDate.from(x.date), RATES),
				purpose: "Business",
				rate: getMileageRate(PlainDate.from(x.date), RATES),
			}))}
			onPayClick={handlePayment}
			monthsCount={monthCount}
			reportId={reportId}
			isProcessingPayment={isProcessingPayment}
		/>
	)
}
const vehicleModelToString = (vehicle: VehicleModel): string => {
	return `${vehicle.make} ${vehicle.model} ${vehicle.year}`
}
